import {
  hasPageActionAccess,
  otsPageActions,
  useAuth
} from "@ots-frontend/ots-ui";
import {useTranslation} from "react-i18next";
import {
  IconAlertTriangle,
  IconChartArrowsVertical,
  IconDatabaseExport,
  IconDatabaseImport,
  IconListDetails,
  IconPlus,
  IconQrcode,
  IconReport,
  IconTools,
  TablerIconsProps
} from "@tabler/icons-react";
import {NavigateOptions, useNavigate} from "react-router-dom";
import {
  siteAssignmentTypes,
  useGetContainerLevelSimulationQuery,
  useUpdateContainerLevelSimulationMutation,
} from "@ots-frontend/ots-data";
import {createElement, ReactElement, useMemo, useState} from "react";
import {
  ContainerLevelSimulationDialog,
  DirectoryItem,
  DirectoryPage,
  otsDirectoryGroupLabels,
  otsDirectoryGroupOrder,
} from "@ots-frontend/ots-ui-tw";

type Tool = {
  title: string
  route: {
    to: string
    options?: NavigateOptions
  },
  icon: (props: TablerIconsProps) => ReactElement
  color: string
}

const tools: Tool[] = [
  {
    title: 'Company Assignments - Bulk Create',
    route: {
      to: '/site-assignments/company/bulk-form',
      options: {
        state: {
          type: siteAssignmentTypes.company
        }
      }
    },
    icon: IconTools,
    color: 'dark'
  },
  {
    title: 'Delivery Site Assignments - Bulk Create',
    route: {
      to: '/site-assignments/site/bulk-form',
      options: {
        state: {
          type: siteAssignmentTypes.deliverySite
        }
      }
    },
    icon: IconTools,
    color: 'dark'
  },
  {
    title: 'Export - Containers',
    route: {
      to: '/export/containers',
    },
    icon: IconDatabaseExport,
    color: 'dark'
  },
  {
    title: 'Import - Containers',
    route: {
      to: '/import/containers',
    },
    icon: IconDatabaseImport,
    color: 'dark'
  },
  {
    title: 'Import - Meters',
    route: {
      to: '/import/meters',
    },
    icon: IconDatabaseImport,
    color: 'dark'
  },
  {
    title: 'Import - Site to Site Associations (Mileage)',
    route: {
      to: '/import/site-site-assocs',
    },
    icon: IconDatabaseImport,
    color: 'dark'
  },
  {
    title: 'Object Mapping - Create',
    route: {
      to: '/object-mappings/form'
    },
    icon: IconPlus,
    color: 'dark'
  },
  {
    title: 'Object Combo Mapping - Create',
    route: {
      to: '/object-combo-mappings/form'
    },
    icon: IconPlus,
    color: 'dark'
  },
  {
    title: 'Report - Company Disposal',
    route: {
      to: '/reports/company-disposal'
    },
    icon: IconReport,
    color: 'dark'
  },
  {
    title: 'Report - Dispute',
    route: {
      to: '/reports/dispute'
    },
    icon: IconReport,
    color: 'dark'
  },
  {
    title: 'Report - On Site Time',
    route: {
      to: '/reports/on-site-time'
    },
    icon: IconReport,
    color: 'dark'
  },
  {
    title: 'Restriction - Create',
    route: {
      to: '/restrictions/form'
    },
    icon: IconAlertTriangle,
    color: 'dark'
  },
  {
    title: 'System Process Logs - Search',
    route: {
      to: '/system-process-executions'
    },
    icon: IconListDetails,
    color: 'dark'
  },
];

export const ToolsPage = () => {
  const {hasAccess} = useAuth()
  const {t} = useTranslation(["translation", "common"])
  const navigate = useNavigate()
  const [simulationOpen, setSimulationOpen] = useState(false);
  const {data: simulationStatus, isSuccess: simulationAvailable} = useGetContainerLevelSimulationQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [updateSimulation] = useUpdateContainerLevelSimulationMutation();

  const showSimulation = simulationAvailable && simulationStatus?.available;

  const directoryItems = useMemo<DirectoryItem[]>(() => otsPageActions
    .filter((action) => hasPageActionAccess(action, hasAccess))
    .map((action) => ({
      id: action.id,
      title: t(action.title.id, action.title.defaultMessage),
      description: action.description ? t(action.description.id, action.description.defaultMessage) : undefined,
      href: action.path,
      keywords: action.keywords,
      group: action.group,
      icon: action.icon ? createElement(action.icon.component, {...action.icon.props, stroke: 1.5}) : undefined,
    })), [hasAccess, t]);

  const utilityItems = useMemo<DirectoryItem[]>(() => {
    const items: DirectoryItem[] = hasAccess("super")
      ? tools.map((tool) => ({
        id: `tool-${tool.title}`,
        title: tool.title,
        description: "Open administrative tool",
        keywords: ["tool", ...tool.title.toLocaleLowerCase().split(/\W+/)],
        icon: createElement(tool.icon, {stroke: 1.5}),
        onSelect: () => navigate(tool.route.to, tool.route.options),
      }))
      : [];

    if (hasAccess("app_access_view")) {
      items.unshift({
        id: "tool-app-access",
        title: "App Access",
        description: "Create and deliver app install or App Identity setup links.",
        keywords: ["app", "install", "identity", "setup", "qr"],
        icon: createElement(IconQrcode, {stroke: 1.5}),
        onSelect: () => navigate("/app-access"),
      });
    }

    if (showSimulation) {
      items.unshift({
        id: "tool-container-level-simulation",
        title: "Simulated Container Levels",
        description: "Control the non-persistent feed used by container dashboards and live boards.",
        keywords: ["simulation", "mock", "container", "level", "live"],
        icon: createElement(IconChartArrowsVertical, {stroke: 1.5}),
        status: (
          <span className={[
            "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
            simulationStatus.enabled
              ? "bg-[var(--ots-warning-container)] text-[var(--ots-on-warning-container)]"
              : "bg-[var(--ots-neutral-container)] text-[var(--ots-on-neutral-container)]",
          ].join(" ")}>
            {simulationStatus.enabled ? "On" : "Off"}
          </span>
        ),
        onSelect: () => setSimulationOpen(true),
      });
    }

    return items;
  }, [hasAccess, navigate, showSimulation, simulationStatus]);

  return (
    <>
      <DirectoryPage
        items={directoryItems}
        utilityItems={utilityItems}
        groupLabels={otsDirectoryGroupLabels}
        groupOrder={otsDirectoryGroupOrder}
        onNavigate={(href) => navigate(href)}
        brand="hub-go"
        subtitle="Browse operational records, configuration, and the tools available in HubGo."/>
      {showSimulation && (
        <ContainerLevelSimulationDialog
          open={simulationOpen}
          status={simulationStatus}
          brand="hub-go"
          onClose={() => setSimulationOpen(false)}
          onSave={async (update) => {
            await updateSimulation(update).unwrap();
          }}/>
      )}
    </>
  );
};
